import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { SequenceValidator } from './SequenceValidator';
import { coffeeRecipes, ingredients } from '../data/coffeeRecipes';
import CoffeeGrinder from './CoffeeGrinder';
import CoffeeTamper from './CoffeeTamper';
import EspressoExtraction from './EspressoExtraction';

const { FiCoffee, FiDroplet, FiPlus, FiCheck, FiX, FiUser, FiAlertTriangle } = FiIcons;

function FirstPersonView({ currentOrder, onOrderComplete, gameState, unlockedItems, onCustomerNameChange }) {
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [validator, setValidator] = useState(null);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  
  // New brewing states
  const [showGrinder, setShowGrinder] = useState(false);
  const [showTamper, setShowTamper] = useState(false);
  const [showExtraction, setShowExtraction] = useState(false);
  const [grindResult, setGrindResult] = useState(null);
  const [tampResult, setTampResult] = useState(null);
  const [extractionResult, setExtractionResult] = useState(null);

  // Initialize validator when order changes
  useEffect(() => {
    if (currentOrder?.drink && coffeeRecipes[currentOrder.drink]) {
      setValidator(new SequenceValidator(currentOrder.drink));
      setSelectedIngredients([]);
      setCurrentFeedback(null);
      // Reset brewing states
      setGrindResult(null);
      setTampResult(null);
      setExtractionResult(null);
      setCustomerName(currentOrder.name || '');
    }
  }, [currentOrder]);

  // Update customer name when currentOrder.name changes
  useEffect(() => {
    if (currentOrder?.name) {
      setCustomerName(currentOrder.name);
    }
  }, [currentOrder?.name]);

  // Map drink names to recipe keys
  const getRecipeKey = (drinkName) => {
    const mapping = {
      'espresso': 'espresso',
      'americano': 'americano',
      'latte': 'latte',
      'cappuccino': 'cappuccino',
      'macchiato': 'macchiato',
      'mocha': 'mocha',
      'matcha_tea': 'matcha_shot',
      'matcha_latte': 'matcha_latte',
      'matcha_frappuccino': 'matcha_frappuccino'
    };
    return mapping[drinkName] || drinkName;
  };

  // Available ingredients - using the EXACT IDs from coffeeRecipes.js
  const availableIngredients = [
    { id: 'grind_beans', name: 'Grind Beans', emoji: '⚙️', color: 'from-amber-800 to-amber-900', unlocked: true, isProcess: true },
    { id: 'espresso', name: 'Espresso', emoji: '☕', color: 'from-amber-600 to-amber-800', unlocked: true },
    { id: 'steamed_milk', name: 'Steamed Milk', emoji: '🥛', color: 'from-blue-400 to-blue-600', unlocked: true },
    { id: 'milk_foam', name: 'Milk Foam', emoji: '🫧', color: 'from-gray-200 to-gray-400', unlocked: true },
    { id: 'light_foam', name: 'Light Foam', emoji: '🤍', color: 'from-gray-100 to-gray-300', unlocked: true },
    { id: 'microfoam_milk', name: 'Microfoam', emoji: '☁️', color: 'from-blue-200 to-blue-400', unlocked: true },
    { id: 'hot_water', name: 'Hot Water', emoji: '💧', color: 'from-blue-600 to-blue-800', unlocked: true },
    { id: 'cold_water', name: 'Cold Water', emoji: '🌊', color: 'from-cyan-600 to-cyan-800', unlocked: true },
    { id: 'matcha_powder', name: 'Matcha', emoji: '🍃', color: 'from-green-500 to-green-700', unlocked: unlockedItems.includes('matcha_powder') },
    { id: 'whisk', name: 'Whisk', emoji: '🥄', color: 'from-yellow-400 to-yellow-600', unlocked: unlockedItems.includes('matcha_powder') },
    { id: 'ice', name: 'Ice', emoji: '🧊', color: 'from-cyan-400 to-cyan-600', unlocked: unlockedItems.includes('ice') },
    { id: 'chocolate_syrup', name: 'Chocolate', emoji: '🍫', color: 'from-amber-800 to-amber-900', unlocked: true },
    { id: 'whipped_cream', name: 'Whipped Cream', emoji: '🍦', color: 'from-gray-50 to-gray-200', unlocked: true },
    { id: 'cold_milk', name: 'Cold Milk', emoji: '🥛', color: 'from-blue-300 to-blue-500', unlocked: true }
  ];

  const addIngredient = async (ingredientId) => {
    if (gameState !== 'playing' || !currentOrder || !validator || isProcessing) return;

    // Handle special brewing processes
    if (ingredientId === 'grind_beans') {
      setShowGrinder(true);
      return;
    }

    if (ingredientId === 'espresso' && !grindResult) {
      // Force grinding first for espresso drinks
      setCurrentFeedback({
        success: false,
        message: 'You need to grind beans first!',
        hint: 'Tap the grinder to prepare fresh grounds.'
      });
      setTimeout(() => setCurrentFeedback(null), 3000);
      return;
    }

    // Haptic feedback for mobile PWA
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }

    setIsProcessing(true);

    // Simulate processing time based on ingredient
    const processingTime = {
      'espresso': 2000,
      'steamed_milk': 3000,
      'milk_foam': 2500,
      'light_foam': 2000,
      'microfoam_milk': 3500,
      'matcha_powder': 1000,
      'whisk': 2000,
      'hot_water': 1000,
      'ice': 500,
      'chocolate_syrup': 1500,
      'whipped_cream': 1000,
      'cold_water': 1000,
      'cold_milk': 2000
    };

    const delay = processingTime[ingredientId] || 1500;

    setTimeout(() => {
      const result = validator.validateStep(ingredientId);
      setCurrentFeedback(result);

      if (result.success) {
        setSelectedIngredients(prev => [...prev, ingredientId]);
        if (result.isComplete) {
          // Drink is complete
          setTimeout(() => {
            serveDrink();
          }, 1000);
        }
      }

      setIsProcessing(false);

      // Clear feedback after 3 seconds
      setTimeout(() => {
        setCurrentFeedback(null);
      }, 3000);
    }, delay);
  };

  const handleGrindComplete = (result) => {
    setGrindResult(result);
    setShowGrinder(false);
    
    // Automatically proceed to tamping for espresso-based drinks
    if (currentOrder?.drink.includes('espresso') || 
        ['latte', 'cappuccino', 'macchiato', 'americano', 'mocha'].includes(currentOrder?.drink)) {
      setTimeout(() => setShowTamper(true), 500);
    }

    setCurrentFeedback({
      success: true,
      message: `Beans ground to ${result.fineness} - ${result.quality} quality!`,
      hint: result.quality === 'perfect' ? 'Excellent grinding technique!' : 'Good job! Ready for tamping.'
    });

    setTimeout(() => setCurrentFeedback(null), 3000);
  };

  const handleTampComplete = (result) => {
    setTampResult(result);
    setShowTamper(false);
    
    // Automatically proceed to extraction for espresso
    setTimeout(() => setShowExtraction(true), 500);

    setCurrentFeedback({
      success: true,
      message: `${result.quality} tamp with ${Math.round(result.pressure)} lbs pressure!`,
      hint: result.quality === 'perfect' ? 'Perfect tamping technique!' : 'Ready for extraction!'
    });

    setTimeout(() => setCurrentFeedback(null), 3000);
  };

  const handleExtractionComplete = (result) => {
    setExtractionResult(result);
    setShowExtraction(false);
    
    // Add espresso to ingredients automatically
    setSelectedIngredients(prev => [...prev, 'espresso']);
    
    // Continue with recipe validation
    if (validator) {
      const validationResult = validator.validateStep('espresso');
      setCurrentFeedback({
        success: true,
        message: `${result.quality} espresso shot extracted! (${(result.time/1000).toFixed(1)}s, ${Math.round(result.volume)}ml)`,
        hint: validationResult.isComplete ? 'Drink complete!' : 'Continue with the recipe!'
      });

      if (validationResult.isComplete) {
        setTimeout(() => serveDrink(), 1000);
      }
    }

    setTimeout(() => setCurrentFeedback(null), 4000);
  };

  const serveDrink = () => {
    if (!currentOrder || !validator) return;

    // Haptic feedback for completion
    if (navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }

    const feedback = validator.getDetailedFeedback();
    
    // Factor in brewing process quality
    let qualityBonus = 0;
    if (grindResult?.quality === 'perfect') qualityBonus += 0.1;
    if (tampResult?.quality === 'perfect') qualityBonus += 0.1;
    if (extractionResult?.quality === 'perfect') qualityBonus += 0.15;

    const finalScore = Math.min(1, feedback.score + qualityBonus);

    const orderWithCustomName = customerName ? { ...currentOrder, name: customerName } : currentOrder;

    // Enhanced feedback with brewing details
    const enhancedFeedback = {
      ...feedback,
      score: finalScore,
      brewingDetails: {
        grind: grindResult,
        tamp: tampResult,
        extraction: extractionResult
      }
    };

    // Call the order complete callback
    onOrderComplete(orderWithCustomName, finalScore, enhancedFeedback);

    // Reset state
    setSelectedIngredients([]);
    setValidator(null);
    setCurrentFeedback(null);
    setCustomerName('');
    setShowNameInput(false);
    setGrindResult(null);
    setTampResult(null);
    setExtractionResult(null);
  };

  const resetDrink = () => {
    if (validator) {
      validator.reset();
    }
    setSelectedIngredients([]);
    setCurrentFeedback(null);
    setGrindResult(null);
    setTampResult(null);
    setExtractionResult(null);
  };

  const handleSaveName = () => {
    setShowNameInput(false);
    if (onCustomerNameChange) {
      onCustomerNameChange(customerName);
    }
  };

  // Get current recipe and next step
  const currentRecipe = currentOrder ? coffeeRecipes[getRecipeKey(currentOrder.drink)] : null;
  const nextStep = validator && currentRecipe ? currentRecipe.sequence[validator.currentStep] : null;

  return (
    <div className="relative w-full h-screen bg-gradient-to-b from-amber-50 to-orange-100 overflow-hidden">
      {/* Coffee Shop Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 right-0 h-3/5 bg-gradient-to-b from-amber-100 to-amber-200">
          <div className="absolute top-6 left-2 right-2 h-4 bg-gradient-to-r from-amber-800 to-amber-900 rounded shadow-lg"></div>
          <div className="absolute top-12 left-4 right-4 h-3 bg-gradient-to-r from-amber-700 to-amber-800 rounded shadow-md"></div>
          
          {/* Menu Board */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-20 h-12 bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg shadow-xl z-5">
            <div className="p-1 text-white text-xs">
              <div className="text-center font-bold mb-1 text-xs">☕ MENU</div>
              <div className="space-y-0.5 text-xs">
                <div className="text-xs">Espresso $3</div>
                <div className="text-xs">Latte $4</div>
                <div className="text-green-300 text-xs">Matcha $5</div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-amber-900 to-amber-700 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-b from-amber-600 to-amber-700 rounded-t-lg"></div>
          <div className="absolute top-3 left-0 right-0 bottom-0 bg-gradient-to-br from-amber-800 to-amber-900 opacity-80">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '15px 15px'
            }}></div>
          </div>
        </div>
      </div>

      {/* Order Ticket - MOVED ANOTHER 10% HIGHER (now -25% total) */}
      <AnimatePresence>
        {currentOrder && currentRecipe && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-20"
            style={{ top: '-25%' }}
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
          >
            <div className="w-56 bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-xl border-2 border-dashed border-amber-400">
              {/* Customer Info - Ultra Compact */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-xs mr-1.5">
                    {(customerName || currentOrder.name)[0]}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-xs leading-tight">{customerName || currentOrder.name}</p>
                    <p className="text-xs text-gray-500 leading-tight">#{currentOrder.id.toString().slice(-4)}</p>
                  </div>
                </div>
                <div className="bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full text-xs font-medium">
                  {currentOrder.patience}s
                </div>
              </div>

              {/* Drink Info - Ultra Compact */}
              <div className="bg-gray-50 rounded p-1.5 mb-1">
                <div className="flex items-center mb-1">
                  <span className="text-lg mr-1.5">
                    {currentOrder.drink.includes('matcha') ? '🍃' : '☕'}
                  </span>
                  <div className="flex-1">
                    <span className="font-bold capitalize text-gray-800 text-xs">
                      {currentRecipe.name}
                    </span>
                    {nextStep && (
                      <div className="bg-blue-50 rounded p-1 mt-0.5">
                        <p className="text-xs text-blue-600 font-medium leading-tight">
                          Next: {ingredients[nextStep]?.name || nextStep.replace('_', ' ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <button
                    className="bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full text-xs flex items-center"
                    onClick={() => setShowNameInput(!showNameInput)}
                  >
                    ✏️ Name
                  </button>
                  <div className="text-gray-600 font-medium">
                    {validator ? Math.round((validator.currentStep / currentRecipe.sequence.length) * 100) : 0}%
                  </div>
                </div>
              </div>

              {/* Brewing Process Status */}
              {(grindResult || tampResult || extractionResult) && (
                <div className="bg-green-50 rounded p-1.5 mb-1">
                  <p className="text-xs font-medium text-green-700 mb-0.5">Brewing Process:</p>
                  <div className="flex flex-wrap gap-0.5 text-xs">
                    {grindResult && (
                      <span className="bg-green-100 text-green-700 px-1 py-0.5 rounded">
                        ⚙️ {grindResult.quality}
                      </span>
                    )}
                    {tampResult && (
                      <span className="bg-green-100 text-green-700 px-1 py-0.5 rounded">
                        🔨 {tampResult.quality}
                      </span>
                    )}
                    {extractionResult && (
                      <span className="bg-green-100 text-green-700 px-1 py-0.5 rounded">
                        ☕ {extractionResult.quality}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Recipe Steps Preview - Ultra Compact */}
              <div className="bg-amber-50 rounded p-1.5">
                <p className="text-xs font-medium text-amber-700 mb-0.5">Steps:</p>
                <div className="flex flex-wrap gap-0.5">
                  {currentRecipe.sequence.map((step, index) => (
                    <div
                      key={index}
                      className={`text-xs px-1 py-0.5 rounded text-center ${
                        index < validator?.currentStep
                          ? 'bg-green-100 text-green-700'
                          : index === validator?.currentStep
                          ? 'bg-yellow-100 text-yellow-700 font-bold'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {ingredients[step]?.name?.split(' ')[0] || step.replace('_', ' ').split(' ')[0]}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Brewing Process Modals */}
      <CoffeeGrinder
        isActive={showGrinder}
        onGrindComplete={handleGrindComplete}
        targetFineness="medium"
      />

      <CoffeeTamper
        isActive={showTamper}
        onTampComplete={handleTampComplete}
        grindQuality={grindResult?.quality || 'medium'}
      />

      <EspressoExtraction
        isActive={showExtraction}
        onExtractionComplete={handleExtractionComplete}
        grindQuality={grindResult?.quality || 'medium'}
        tampQuality={tampResult?.quality || 'good'}
      />

      {/* Customer Name Input */}
      <AnimatePresence>
        {showNameInput && (
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-30"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-xl w-56">
              <div className="flex items-center mb-2">
                <SafeIcon icon={FiUser} className="text-purple-500 mr-2 text-sm" />
                <span className="font-medium text-gray-800 text-sm">Custom Name</span>
              </div>
              <input
                type="text"
                placeholder="Customer's name..."
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full px-2 py-1.5 border-2 border-purple-200 rounded-lg focus:border-purple-400 focus:outline-none text-sm"
                maxLength={15}
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button
                  className="flex-1 bg-gray-200 text-gray-600 py-1.5 rounded-lg text-sm"
                  onClick={() => {
                    setCustomerName(currentOrder.name);
                    setShowNameInput(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 bg-purple-500 text-white py-1.5 rounded-lg text-sm"
                  onClick={handleSaveName}
                >
                  Save
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Coffee Cup Visualization - Positioned below center */}
      <div className="absolute bottom-60 left-1/2 transform -translate-x-1/2 z-15">
        <div className="relative">
          <div className="w-32 h-36 bg-gradient-to-b from-gray-100 to-gray-200 rounded-b-3xl border-4 border-gray-300 relative overflow-hidden shadow-xl">
            <AnimatePresence>
              {selectedIngredients.includes('espresso') && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-amber-800 to-amber-600"
                  initial={{ height: 0 }}
                  animate={{ height: '30%' }}
                  transition={{ duration: 0.5 }}
                />
              )}
              {selectedIngredients.includes('matcha_powder') && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-600 to-green-400"
                  initial={{ height: 0 }}
                  animate={{ height: '25%' }}
                  transition={{ duration: 0.5 }}
                />
              )}
              {(selectedIngredients.includes('steamed_milk') || 
                selectedIngredients.includes('microfoam_milk') || 
                selectedIngredients.includes('cold_milk')) && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-100 to-white"
                  initial={{ 
                    height: selectedIngredients.includes('espresso') || selectedIngredients.includes('matcha_powder') 
                      ? '30%' : '0%' 
                  }}
                  animate={{ height: '75%' }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                />
              )}
              {(selectedIngredients.includes('milk_foam') || selectedIngredients.includes('light_foam')) && (
                <motion.div
                  className="absolute top-0 left-0 right-0 bg-gradient-to-b from-gray-50 to-white h-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                />
              )}
              {selectedIngredients.includes('ice') && (
                <motion.div
                  className="absolute top-2 left-2 right-2 h-4 bg-gradient-to-b from-cyan-100 to-cyan-200 rounded opacity-60"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 0.6, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                />
              )}
              {selectedIngredients.includes('whipped_cream') && (
                <motion.div
                  className="absolute top-0 left-0 right-0 bg-gradient-to-b from-white to-gray-50 h-6"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                />
              )}
            </AnimatePresence>
          </div>
          <div className="w-8 h-6 bg-gray-300 rounded-r-lg absolute right-0 top-1/2 transform -translate-y-1/2"></div>
        </div>
      </div>

      {/* PWA-Optimized Bottom Ingredients Bar - FIXED POSITIONING */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-gray-200" 
           style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        
        {/* Action Buttons Row */}
        {currentOrder && (
          <div className="flex gap-2 p-2 border-b border-gray-200">
            <motion.button
              className="flex-1 bg-gradient-to-r from-red-400 to-red-500 text-white py-2.5 rounded-xl font-bold shadow-lg text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={resetDrink}
              disabled={selectedIngredients.length === 0 || isProcessing}
            >
              <SafeIcon icon={FiX} className="inline mr-1 text-sm" />
              Reset
            </motion.button>

            {validator && validator.currentStep >= currentRecipe?.sequence.length && (
              <motion.button
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2.5 rounded-xl font-bold shadow-lg text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={serveDrink}
                disabled={isProcessing}
              >
                <SafeIcon icon={FiCheck} className="inline mr-1 text-sm" />
                Serve Drink
              </motion.button>
            )}
          </div>
        )}

        {/* PWA-Optimized Ingredients Horizontal Scroll */}
        <div className="p-2 pb-3">
          <div className="flex items-center mb-2">
            <h3 className="text-sm font-bold text-gray-700">Ingredients</h3>
            {nextStep && (
              <div className="ml-2 bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium">
                Next: {ingredients[nextStep]?.name || nextStep.replace('_', ' ')}
              </div>
            )}
          </div>

          {/* Single Line Horizontal Scroll - PWA Optimized */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide touch-pan-x" 
               style={{ 
                 scrollbarWidth: 'none', 
                 msOverflowStyle: 'none', 
                 WebkitOverflowScrolling: 'touch' 
               }}>
            {availableIngredients.map((ingredient) => {
              const shouldGlow = nextStep && ingredient.id === nextStep;
              const isUnlocked = ingredient.unlocked;
              const isAdded = selectedIngredients.includes(ingredient.id);
              
              // Special logic for grinding process
              const canClick = isUnlocked && (
                ingredient.id === 'grind_beans' || // Always allow grinding
                (shouldGlow || !currentOrder) || // Normal flow
                (ingredient.id === 'espresso' && grindResult && tampResult && extractionResult) // Espresso after brewing
              );

              return (
                <motion.button
                  key={ingredient.id}
                  className={`
                    flex-shrink-0 w-16 h-16 rounded-2xl shadow-lg relative overflow-hidden border-2 touch-manipulation
                    ${!isUnlocked ? 'opacity-40 cursor-not-allowed' : 'opacity-100'}
                    ${shouldGlow ? 'ring-4 ring-yellow-300 ring-opacity-75 border-yellow-400' : 'border-white/30'}
                    ${isAdded ? 'ring-2 ring-green-400 border-green-400' : ''}
                    ${canClick ? 'cursor-pointer' : 'cursor-not-allowed'}
                    ${ingredient.isProcess ? 'ring-2 ring-amber-400 border-amber-400' : ''}
                  `}
                  whileHover={canClick ? { scale: 1.05 } : {}}
                  whileTap={canClick ? { scale: 0.95 } : {}}
                  onClick={() => {
                    if (canClick) {
                      addIngredient(ingredient.id);
                    }
                  }}
                  disabled={!canClick || isProcessing}
                  animate={shouldGlow ? {
                    boxShadow: [
                      '0 0 0 0 rgba(255,255,0,0.7)',
                      '0 0 0 10px rgba(255,255,0,0)'
                    ]
                  } : {}}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <div className={`w-full h-full bg-gradient-to-b ${ingredient.color} rounded-xl relative overflow-hidden`}>
                    <div className="absolute inset-1 bg-black/10 rounded-lg flex flex-col items-center justify-center">
                      <span className="text-white text-xl mb-1">{ingredient.emoji}</span>
                      
                      {/* Processing indicator */}
                      {isProcessing && shouldGlow && (
                        <motion.div
                          className="absolute top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-3 bg-white/60 rounded-full blur-sm"
                          initial={{ opacity: 0, y: 2 }}
                          animate={{ opacity: 1, y: -2 }}
                          transition={{ duration: 0.3, repeat: Infinity, repeatType: "reverse" }}
                        />
                      )}

                      {/* Added indicator */}
                      {isAdded && (
                        <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                          <SafeIcon icon={FiCheck} className="text-white text-xs" />
                        </div>
                      )}

                      {/* Process indicator */}
                      {ingredient.isProcess && (
                        <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">⚙</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ingredient Name - Positioned above the button */}
                  <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-700 text-center whitespace-nowrap font-medium bg-white/90 px-1.5 py-0.5 rounded shadow-md">
                    {ingredient.name}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feedback System */}
      <AnimatePresence>
        {currentFeedback && (
          <motion.div
            className={`absolute top-1/2 left-2 right-2 rounded-xl p-3 shadow-xl z-50 ${
              currentFeedback.success ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="flex items-center">
              <SafeIcon icon={currentFeedback.success ? FiCheck : FiX} className="text-lg mr-2" />
              <div>
                <p className="font-bold text-sm">{currentFeedback.message}</p>
                {currentFeedback.hint && (
                  <p className="text-xs mt-1 opacity-90">{currentFeedback.hint}</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No Order State */}
      {!currentOrder && (
        <div className="absolute inset-0 flex items-center justify-center z-15">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl text-center">
            <SafeIcon icon={FiCoffee} className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-400 mb-2">Ready to Serve</h3>
            <p className="text-gray-500 text-sm">Waiting for customers...</p>
          </div>
        </div>
      )}

      {/* Processing Status */}
      <AnimatePresence>
        {isProcessing && (
          <motion.div
            className="absolute top-1/3 left-2 right-2 bg-amber-500 text-white rounded-xl p-2.5 shadow-xl z-30"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <div className="text-center">
              <p className="font-medium text-sm mb-1.5">
                Preparing {ingredients[nextStep]?.name || 'ingredient'}...
              </p>
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <motion.div
                  className="bg-white h-1.5 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2 }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Visual Guide Arrow - Points to next ingredient in scroll bar */}
      <AnimatePresence>
        {nextStep && currentOrder && !grindResult && nextStep === 'espresso' && (
          <motion.div
            className="absolute bottom-40 left-1/2 transform -translate-x-1/2 z-45 pointer-events-none"
            initial={{ opacity: 0, y: 10 }}
            animate={{ 
              opacity: 1, 
              y: [0, -8, 0], 
              scale: [1, 1.1, 1] 
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              y: { duration: 1.5, repeat: Infinity }, 
              scale: { duration: 1.5, repeat: Infinity } 
            }}
          >
            <div className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold shadow-lg flex items-center">
              👇 Start with grinding beans!
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {nextStep && currentOrder && grindResult && !isProcessing && (
          <motion.div
            className="absolute bottom-40 left-1/2 transform -translate-x-1/2 z-45 pointer-events-none"
            initial={{ opacity: 0, y: 10 }}
            animate={{ 
              opacity: 1, 
              y: [0, -8, 0], 
              scale: [1, 1.1, 1] 
            }}
            exit={{ opacity: 0 }}
            transition={{ 
              y: { duration: 1.5, repeat: Infinity }, 
              scale: { duration: 1.5, repeat: Infinity } 
            }}
          >
            <div className="bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold shadow-lg flex items-center">
              👇 Tap glowing ingredient!
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default FirstPersonView;